import { Authenticated, CanAccess } from "@refinedev/core"
import { AntdInferencer } from "@refinedev/inferencer/antd"
import { Navigate, Outlet, Route } from "react-router-dom"
import { AccountList, AccountCreate, AccountShow } from "../pages/accounts"
import { FormCreate } from "../pages/forms/create"
import { FormEdit } from "../pages/forms/edit"
import { FormShow } from "../pages/forms/show"
import { ProductCreate } from "../pages/products/create"
import { ProductEdit } from "../pages/products/edit"
import { ProductShow } from "../pages/products/show"
import { UserCreate } from "../pages/users/create"
import { FormList } from "../pages/forms/list"
import { CatchAllNavigate } from "@refinedev/react-router-v6";
import { ResponsiveLayout } from "../components/layout/MobileLayout"
import { OrderShow } from "../pages/orders/show"
import { NotFound, Unauthorized } from "../components/errors"
import { TransactionList } from "../pages/transactions/list"
import { UserEdit } from "../pages/users/edit"
import { SavList } from "../pages/orders/list-sav";
import { PricingPlanCreate, PricingPlanEdit, PricingPlanShow } from "../pages/pricing-plans";
import { Dashboard } from "../pages/dashboard";
import { NotificationsList } from "../pages/notifications"
import { CatalogPage } from "../pages/catalog"
import { TeamPage } from "../pages/team"
import { OrganizationConfigurationPage } from "../pages/configuration/organization"
import { WorkflowConfigurationPage } from "../pages/configuration/workflow"
import { OrderList } from "../pages/orders/list"
import { AutomationPage } from "../pages/automation"
import { TenantList, TenantCreate, TenantEdit, TenantShow } from "../pages/tenants"
import { AccountRating } from "../pages/account-rating"
import { ServiceProviderCreate, ServiceProviderEdit, ServiceProviderShow } from "../pages/service-providers"
import { ClientList, ClientShow } from "../pages/clients"
import UserManagementAntD from "../pages/users/userListMockup"
import AssignmentWidget from "../pages/orders/assignTunerMockup"
import TunerRequestDetailAntD from "../pages/orders/showMockup"

export const AdminRoutes = (
    <Route
        element={
            <Authenticated
                key="authenticated-inner"
                fallback={<CatchAllNavigate to="/login" />}
            >
                <CanAccess
                    fallback={<Unauthorized />}
                >
                    <ResponsiveLayout>
                        <Outlet />
                    </ResponsiveLayout>
                </CanAccess>
            </Authenticated>
        }
    >
        <Route
            path="/"
            index
            element={<Dashboard />}
        />
        <Route path="orders">
            <Route index element={<OrderList />} />
            <Route path="create" element={<AntdInferencer />} />
            <Route path="edit/:id" element={<AntdInferencer />} />
            <Route path="show/:id" element={<OrderShow />} />
            <Route path="mockup" element={<AssignmentWidget />} />
            <Route path="order-mockup" element={<TunerRequestDetailAntD />} />
        </Route>
        <Route path="sav">
            <Route index element={<SavList />} />
        </Route>
        <Route path="accounts">
            <Route index element={<AccountList />} />
            <Route path="create" element={<AccountCreate />} />
            <Route path="show/:id" element={<AccountShow />} />
        </Route>
        {/* ---- Hub pages ---- */}
        <Route path="catalog" element={<CatalogPage />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="configuration">
            <Route index element={<Navigate to="/configuration/organization" replace />} />
            <Route path="organization" element={<OrganizationConfigurationPage />} />
            <Route path="workflow" element={<WorkflowConfigurationPage />} />
        </Route>

        {/* Sub-routes of the hubs keep their own pages (create/edit/show flows). */}
        <Route path="users">
            <Route index element={<Navigate to="/team?tab=users" replace />} />
            <Route path="create" element={<UserCreate />} />
            <Route path="edit/:id" element={<UserEdit />} />
            <Route path="show/:id" element={<AntdInferencer />} />
            <Route path="mockup" element={<UserManagementAntD />} />
        </Route>
        <Route path="user-groups">
            <Route index element={<Navigate to="/team?tab=groups" replace />} />
        </Route>
        <Route path="products">
            <Route index element={<Navigate to="/catalog?tab=products" replace />} />
            <Route path="create" element={<ProductCreate />} />
            <Route path="edit/:id" element={<ProductEdit />} />
            <Route path="show/:id" element={<ProductShow />} />
        </Route>
        <Route path="pricing-plans">
            <Route index element={<Navigate to="/catalog?tab=plans" replace />} />
            <Route path="create" element={<PricingPlanCreate />} />
            <Route path="edit/:id" element={<PricingPlanEdit />} />
            <Route path="show/:id" element={<PricingPlanShow />} />
        </Route>
        <Route path="forms">
            <Route index element={<FormList />} />
            <Route path="create" element={<FormCreate />} />
            <Route path="edit/:id" element={<FormEdit />} />
            <Route path="show/:id" element={<FormShow />} />
        </Route>
        <Route path="attachments">
            <Route index element={<AntdInferencer />} />
            <Route path="create" element={<AntdInferencer />} />
            <Route path="edit/:id" element={<AntdInferencer />} />
        </Route>
        <Route path="transactions">
            <Route index element={<TransactionList />} />
            <Route path="show/:id" element={<AntdInferencer />} />
        </Route>

        {/* `/settings` is declared once, in customerRoutes — it is the personal
            hub for every role. Redeclaring it here shadowed nothing (CustomerRoutes
            renders first) but would 403 admins the day access control tightens. */}
        <Route path="settings/general" element={<Navigate to="/configuration/organization?tab=general" replace />} />

        <Route path="notifications" element={<NotificationsList />} />

        {/* Automation testbed: kept, and surfaced as a superadmin tab of the
            workflow hub. The direct route stays for bookmarks and Kbar. */}
        <Route path="automation" element={<AutomationPage />} />

        <Route path="automation-tasks">
            <Route index element={<Navigate to="/configuration/workflow?tab=automation" replace />} />
        </Route>

        <Route path="tenants">
            <Route index element={<TenantList />} />
            <Route path="create" element={<TenantCreate />} />
            <Route path="edit/:id" element={<TenantEdit />} />
            <Route path="show/:id" element={<TenantShow />} />
        </Route>

        <Route path="account-rating" element={<AccountRating />} />

        <Route path="service-providers">
            <Route index element={<Navigate to="/configuration/workflow?tab=providers" replace />} />
            <Route path="create" element={<ServiceProviderCreate />} />
            <Route path="edit/:id" element={<ServiceProviderEdit />} />
            <Route path="show/:id" element={<ServiceProviderShow />} />
        </Route>

        {/* ---- Legacy routes, redirected into their hub tab.
               Keep for at least two releases: bookmarks and transactional emails. ---- */}
        <Route path="assignment-rules" element={<Navigate to="/configuration/workflow?tab=assignment" replace />} />

        <Route path="credit-packs" element={<Navigate to="/catalog?tab=credit-packs" replace />} />

        <Route path="invoice-settings" element={<Navigate to="/configuration/organization?tab=invoicing" replace />} />

        <Route path="usage" element={<Navigate to="/configuration/organization?tab=subscription" replace />} />

        <Route path="clients">
            <Route index element={<ClientList />} />
            <Route path="show/:id" element={<ClientShow />} />
        </Route>

        <Route path="*" element={<NotFound />} />
    </Route>
)
